import { useTranslation } from "react-i18next";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ElementType,
  type ReactNode,
} from "react";
import { useSearchParams } from "react-router-dom";
import { AnimatePresence, motion } from "motion/react";
import { ChevronLeft, ChevronRight } from "lucide-react";

import { cn } from "@/utils/cn";
import { Pagination } from "@/components/table/Pagination";

const formatCompactCount = (n: number): string => {
  const abs = Math.abs(n);
  if (abs < 1000) return String(n);
  if (abs < 10_000) return `${(n / 1000).toFixed(1).replace(/\.0$/, "")}k`;
  if (abs < 1_000_000) return `${Math.round(n / 1000)}k`;
  if (abs < 10_000_000)
    return `${(n / 1_000_000).toFixed(1).replace(/\.0$/, "")}M`;
  return `${Math.round(n / 1_000_000)}M`;
};

// ── Pagination config ─────────────────────────────────────────────────────────

export interface PaginationConfig {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  totalItems?: number;
  itemsPerPage?: number;
  hasNextPage?: boolean;
  hasPreviousPage?: boolean;
}

// ── Tab definition ────────────────────────────────────────────────────────────

export interface TabItem {
  /** Unique identifier – also written to the URL as ?<urlParamKey>=<value> */
  value: string;
  label: string;
  /** Optional leading icon rendered before the label */
  icon?: ElementType;
  disabled?: boolean;
  count?: number;
  countColor?: string;

  /**
   * The tab's main content rendered inside the scrollable middle zone.
   * For self-contained tabs the content is responsible for its own scroll and
   * internal pagination (e.g. a table that manages its own footer).
   */
  content: ReactNode;

  selfContained?: boolean;

  /** Extra classes applied to the scrollable content wrapper (e.g. "p-6"). */
  contentClassName?: string;

  /**
   * Pagination config. When provided (and totalPages > 1) a <Pagination>
   * component is rendered in the sticky bottom zone, centred horizontally.
   * Pass undefined to omit pagination for this tab.
   */
  pagination?: PaginationConfig;

  /**
   * Action buttons rendered in the sticky bottom zone (e.g. Save / Cancel).
   * Pass undefined to omit actions for this tab.
   * When both pagination and bottomActions are present the layout is:
   *   [ spacer | Pagination (centred) | actions (right) ]
   */
  bottomActions?: ReactNode;
}

// ── Container props ───────────────────────────────────────────────────────────

interface Props {
  tabs: TabItem[];
  defaultValue: string;
  /** Controlled active tab value */
  value?: string;
  /** Fired whenever the active tab changes */
  onTabChange?: (value: string) => void;
  /**
   * URL search-param key used to persist the active tab.
   * @default "hTab"
   */
  urlParamKey?: string;
  /**
   * Visual style for the tab bar.
   * - "filled"    Active tab gets a coloured background + bottom hr indicator (default).
   * - "underline" Active tab gets a bottom border underline only.
   */
  tabVariant?: "filled" | "underline";
  /** Override/extend the outer container classes (e.g. drop the border when nested in another card). */
  containerClassName?: string;
  /**
   * How the container manages vertical scroll.
   * - "contain" (default) Container is height-bounded (`min-h-0`) and the active
   *   tab owns its own internal scroll. Use for detail/form layouts that fill a
   *   fixed viewport (e.g. full-height 2-pane tabs).
   * - "flow" Container is NOT height-capped; the tab content flows and the outer
   *   page (`<main>`) scrolls the whole thing, with the tab bar sticky at top and
   *   pagination sticky at bottom. Use for list pages so short laptop screens can
   *   scroll to reach all rows (HM-1030).
   */
  scrollMode?: "contain" | "flow";
  /**
   * Trailing controls pinned to the right of the tab bar — the same slot
   * `RoutableTabsLayout` exposes, so a module's settings CTA sits in the same
   * place whichever tab component the page uses. Rendered OUTSIDE the tabs'
   * scroll container, so it never scrolls away or collides with the overflow
   * arrows.
   */
  actions?: ReactNode;
}

// ── Component ─────────────────────────────────────────────────────────────────

export const TabPaginationContainer = ({
  tabs,
  defaultValue,
  value,
  onTabChange,
  urlParamKey = "hTab",
  tabVariant = "filled",
  containerClassName,
  scrollMode = "contain",
  actions,
}: Props) => {
  const { t } = useTranslation(["common"]);
  const isFlow = scrollMode === "flow";
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get(urlParamKey);

  const isValid = (val: string | null | undefined): val is string =>
    !!val && tabs.some((t) => t.value === val);

  const resolve = () => {
    if (isValid(tabFromUrl)) return tabFromUrl;
    if (isValid(value)) return value;
    if (isValid(defaultValue)) return defaultValue;
    return tabs[0]?.value ?? "";
  };

  const [activeTab, setActiveTab] = useState(resolve);

  // Keep in sync with URL / controlled value changes
  useEffect(() => {
    const next = resolve();
    if (next !== activeTab) setActiveTab(next);
  }, [tabFromUrl, value, defaultValue]);

  // Sliding indicator
  const tabBarRef = useRef<HTMLDivElement>(null);
  const tabRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
  const [indicator, setIndicator] = useState({ left: 0, width: 0 });

  useLayoutEffect(() => {
    const bar = tabBarRef.current;
    const btn = tabRefs.current.get(activeTab);
    if (!bar || !btn) return;
    const barRect = bar.getBoundingClientRect();
    const btnRect = btn.getBoundingClientRect();
    setIndicator({
      left: btnRect.left - barRect.left + bar.scrollLeft,
      width: btnRect.width,
    });
  }, [activeTab, tabs]);

  // Horizontal overflow arrows
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const checkOverflow = useCallback(() => {
    const bar = tabBarRef.current;
    if (!bar) return;
    setCanScrollLeft(bar.scrollLeft > 1);
    setCanScrollRight(bar.scrollLeft + bar.clientWidth < bar.scrollWidth - 1);
  }, []);

  useLayoutEffect(() => {
    checkOverflow();
    const bar = tabBarRef.current;
    if (!bar) return;
    const observer = new ResizeObserver(checkOverflow);
    observer.observe(bar);
    return () => observer.disconnect();
  }, [checkOverflow, tabs]);

  const scrollByAmount = (dir: 1 | -1) => {
    tabBarRef.current?.scrollBy({ left: dir * 160, behavior: "smooth" });
  };

  const switchTab = (next: string) => {
    if (next === activeTab) return;
    setActiveTab(next);

    const params = new URLSearchParams(searchParams);
    params.set(urlParamKey, next);
    setSearchParams(params, { replace: true });

    onTabChange?.(next);
  };

  const active = tabs.find((t) => t.value === activeTab);

  // Bottom zone visibility — show pagination bar whenever a config is provided
  const showPagination = active?.pagination !== undefined;
  const showActions = active?.bottomActions !== undefined;
  const showBottom = showPagination || showActions;

  return (
    <div
      className={cn(
        "flex flex-col flex-1 bg-white border rounded-sm",
        !isFlow && "min-h-0",
        containerClassName,
      )}
    >
      {/* ── Top: sticky tab bar ────────────────────────────────────────────── */}
      <div className="sticky -top-2.5 md:-top-4 z-20 bg-white shrink-0 border-b border-border rounded-t-sm overflow-hidden relative flex items-stretch">
        {/* Scroll track for the tabs. The overflow arrows are positioned against
          THIS box, not the whole bar, so `actions` stays clear of them. */}
        <div className="relative flex-1 min-w-0">
          <div
            ref={tabBarRef}
            onScroll={checkOverflow}
            className="relative bg-white overflow-x-auto scrollbar-thin flex items-stretch"
          >
            {tabs.map((tab) => {
              const isActive = tab.value === activeTab;
              const Icon = tab.icon;
              return (
                <button
                  key={tab.value}
                  ref={(el) => {
                    if (el) tabRefs.current.set(tab.value, el);
                    else tabRefs.current.delete(tab.value);
                  }}
                  type="button"
                  disabled={tab.disabled}
                  onClick={() => !tab.disabled && switchTab(tab.value)}
                  className={cn(
                    tabVariant === "filled" && [
                      "group relative overflow-hidden flex items-center gap-2 shrink-0",
                      "px-2 py-2.5 md:px-4 md:py-3",
                      "text-[10px]! md:text-xs! font-semibold uppercase transition-colors duration-200",
                      isActive
                        ? "text-primary"
                        : "text-primary-strong hover:bg-primary/5 hover:text-primary",
                    ],
                    tabVariant === "underline" && [
                      "relative overflow-hidden flex items-center gap-2 shrink-0",
                      "px-3 py-2.5 md:px-5 md:py-3",
                      "text-[10px] md:text-xs font-semibold uppercase tracking-wide",
                      "-mb-px transition-colors duration-200",
                      isActive
                        ? "text-primary bg-primary/3"
                        : "text-gray-500 hover:text-primary hover:bg-primary/2",
                    ],
                    tab.disabled &&
                      "opacity-40 cursor-not-allowed pointer-events-none",
                  )}
                >
                  {/* sliding pill background */}
                  {isActive && tabVariant === "filled" && (
                    <motion.div
                      layoutId="tpc-tab-bg"
                      className="absolute inset-0 bg-primary/10"
                      transition={{
                        type: "spring",
                        stiffness: 380,
                        damping: 30,
                      }}
                    />
                  )}

                  {/* sliding underline */}
                  {isActive && tabVariant === "underline" && (
                    <motion.div
                      layoutId="tpc-tab-underline"
                      className="absolute bottom-0 start-0 end-0 h-[2px] bg-primary"
                      transition={{
                        type: "spring",
                        stiffness: 380,
                        damping: 30,
                      }}
                    />
                  )}

                  {/* icon with scale pop */}
                  {Icon && (
                    <motion.span
                      animate={{ scale: isActive ? 1.08 : 1 }}
                      transition={{
                        type: "spring",
                        stiffness: 400,
                        damping: 25,
                      }}
                      className="relative z-10 shrink-0 flex items-center"
                    >
                      <Icon className="w-4 h-4" />
                    </motion.span>
                  )}

                  <span className="relative z-10">{tab.label}</span>

                  {Boolean(tab.count) && (
                    <span
                      title={String(tab.count)}
                      className={cn(
                        "relative z-10 flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full text-[10px] leading-none text-white tabular-nums",
                        tab.countColor ?? "bg-primary",
                      )}
                    >
                      {formatCompactCount(tab.count!)}
                    </span>
                  )}
                </button>
              );
            })}

            {tabVariant === "filled" && indicator.width > 0 && (
              <motion.div
                className="absolute bottom-0 h-[3px] bg-primary pointer-events-none"
                animate={{ left: indicator.left, width: indicator.width }}
                transition={{ type: "spring", stiffness: 380, damping: 30 }}
              />
            )}
          </div>

          {/* Left overflow affordance: gradient fade + arrow, overlaid — no layout shift */}
          <AnimatePresence>
            {canScrollLeft && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.15 }}
                className="absolute inset-y-0 start-0 z-30 flex items-center pe-6 ps-1 bg-gradient-to-r from-white via-white/90 to-transparent pointer-events-none"
              >
                <button
                  type="button"
                  aria-label={t("common:a11y.scroll_tabs_left")}
                  onClick={() => scrollByAmount(-1)}
                  className="pointer-events-auto flex items-center justify-center w-6 h-6 rounded-full border border-border bg-white text-muted-foreground shadow-sm hover:text-primary hover:border-primary/40 transition-colors"
                >
                  <ChevronLeft className="w-3.5 h-3.5" />
                </button>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Right overflow affordance */}
          <AnimatePresence>
            {canScrollRight && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.15 }}
                className="absolute inset-y-0 end-0 z-30 flex items-center ps-6 pe-1 bg-gradient-to-l from-white via-white/90 to-transparent pointer-events-none"
              >
                <button
                  type="button"
                  aria-label={t("common:a11y.scroll_tabs_right")}
                  onClick={() => scrollByAmount(1)}
                  className="pointer-events-auto flex items-center justify-center w-6 h-6 rounded-full border border-border bg-white text-muted-foreground shadow-sm hover:text-primary hover:border-primary/40 transition-colors"
                >
                  <ChevronRight className="w-3.5 h-3.5" />
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {actions && (
          // Tinted like an active tab so the slot reads as part of the bar, but in
          // the soft-pink `accent` scale (not primary) and with none of the active
          // tab's indicator (sliding pill / 3px underline) — it is not a tab and
          // must never look selected. The button inside stays transparent; this is
          // the only background in the slot.
          // `items-stretch` + no padding here: the control inside spans the full
          // slot so the ENTIRE tinted block is the hover/click target, matching a
          // tab. Padding belongs to the control, not this wrapper.
          <div className="shrink-0 flex items-stretch bg-accent-muted">
            {actions}
          </div>
        )}
      </div>

      {/* ── Middle: content zone ───────────────────────────────────────────── */}
      <AnimatePresence mode="wait">
        {active && (
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.18, ease: "easeInOut" }}
            // Middle (content) zone.
            // - "contain": bounded scroll boundary (min-h-0 + overflow-y-auto) so
            //   content never overflows the fixed-height card; selfContained tabs
            //   also get a flex column so a tab owning its scroll fills the zone.
            // - "flow": no cap and no internal scroll — the tab content flows and
            //   the outer <main> scrolls the whole page (HM-1030).
            className={cn(
              isFlow
                ? active.selfContained
                  ? "flex flex-col flex-1"
                  : "flex-1"
                : active.selfContained
                  ? "flex flex-col flex-1 min-h-0 overflow-y-auto"
                  : "flex-1 min-h-0 overflow-y-auto",
              active.contentClassName,
            )}
          >
            {active.content}
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Bottom: pagination and / or actions bar ────────────────────────── */}
      {showBottom && (
        <div className="sticky -bottom-2.5 md:-bottom-4 z-20 shrink-0 border-t bg-white py-3 px-5 flex items-center rounded-b-sm">
          {showPagination && showActions ? (
            // Both present: pagination centred, actions right-aligned
            <>
              <div className="flex-1" />
              <Pagination
                totalPages={active!.pagination!.totalPages}
                currentPage={active!.pagination!.currentPage}
                onPageChange={active!.pagination!.onPageChange}
                hasNextPage={active!.pagination!.hasNextPage}
                hasPreviousPage={active!.pagination!.hasPreviousPage}
              />
              <div className="flex-1 flex items-center justify-end gap-3">
                {active!.bottomActions}
              </div>
            </>
          ) : showPagination ? (
            // Pagination only: centred, with optional item count on left
            <div className="flex items-center gap-4 mx-auto">
              {active!.pagination!.totalItems !== undefined && (
                <span className="text-xs text-gray-500">
                  {t("common:ui_kit.item_count", {
                    count: active!.pagination!.totalItems,
                  })}
                </span>
              )}
              <Pagination
                totalPages={active!.pagination!.totalPages}
                currentPage={active!.pagination!.currentPage}
                onPageChange={active!.pagination!.onPageChange}
                hasNextPage={active!.pagination!.hasNextPage}
                hasPreviousPage={active!.pagination!.hasPreviousPage}
              />
            </div>
          ) : (
            // Actions only: right-aligned
            <div className="ms-auto flex items-center gap-3">
              {active!.bottomActions}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
